import { useToast } from "contexts/ToastContext";
import { useEffect, useRef, useState } from "react";
import { LoadingPageIcon } from "utils/constant";
import { useTranslation } from "react-i18next";

import { useParams } from "react-router-dom";
import { getStakeholder } from "services/getEmployees";
import { useAuth } from "contexts/AuthContext";
import RegistrarLegislativeDraft from "./registrarLegislativeDraft";
import { CanShow, LEGISLATIVE_DRAFTING_UI_RULES, TeamScope } from "utils/authRules";
import LawImplementationDetail from "./fragments/leftPanel";
import LawImplementationRightPanel from "./fragments/rightPanel";
import { getAuditLawImplementation, getLawImplementation } from "services/lawImplementationService";
import AuditLawImplementationDetail from "./fragments/audit_leftPanel";

const SingleAuditLawImplementation = () => {
    const { t } = useTranslation();
    const { showToast } = useToast();
    const [data, setData] = useState({
        isLoading: true, current_approval_status: "", forward_status: "",
    });
    const { id } = useParams<{ id: string }>();
    const contextData = useAuth();

    useEffect(() => {
        const fetchData = async () => {
            // Guard against invalid route params (e.g. /audit/null) that would hit audit-followups/null/.
            if (!id || id === "null" || id === "undefined") {
                setData((prev) => ({ ...prev, isLoading: false }));
                return;
            }
            try {
                const response = await getAuditLawImplementation(id);
                let user = null;
                if (response.requested_by_id && response.requested_by !== "internal") {
                    try {
                        user = await getStakeholder(response.requested_by_id, contextData.token || contextData?.user?.access_token || "");
                    } catch (err) {
                        console.error("Error fetching stakeholder:", err);
                        showToast(t("FailedToFetchStakeholder") || "Failed to fetch stakeholder", "error");
                    }
                }
                // Pull the selected target list from the related follow-up.
                let identified_law_sectors = response.identified_law_sectors;
                if (response.follow_up) {
                    try {
                        const parent = await getLawImplementation(response.follow_up);
                        identified_law_sectors = parent?.identified_law_sectors ?? identified_law_sectors;
                    } catch (err) {
                        console.error("Error fetching related follow-up:", err);
                    }
                }
                setData({
                    isLoading: false,
                    ...response,
                    identified_law_sectors,
                    requested_by_user: user,
                });
            } catch (error) {
                console.error("Error fetching legal research request:", error);
                showToast(t("FailedToFetchLegalResearchRequest") || "Failed to fetch legal research request.", "error");
                setData((prev) => ({ ...prev, isLoading: false }));
            }
        };

        fetchData();
    }, [id, contextData.token, contextData?.user?.access_token, showToast, t]);

    if (data.isLoading)
        return (
            <div className="flex justify-center items-center h-64">
                {LoadingPageIcon}
            </div>
        );

    // Prosecutor actions on this request go to its team leader only.
    return (
        <TeamScope team={(data as any)?.audit_teams}>
            <>
                <div className="flex h-full bg-gray-100 p-2">
                    <AuditLawImplementationDetail request={data} />
                    {CanShow(LEGISLATIVE_DRAFTING_UI_RULES.edit_reference_number.value, data?.current_approval_status, data?.forward_status) ?
                        <RegistrarLegislativeDraft request={data} /> :
                        contextData.user.roles?.find(role => role === "Registrar") ?
                            <div className="h-fit w-fit overflow-y-scroll scrollbar-hide flex flex-col bg-white rounded-lg shadow-md p-6">
                                <div className="w-fit flex gap-4 mb-4">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                        <path d="M24 9.35869C24 10.4661 23.032 11.413 21.9 11.413H17.2195C17.9854 12.2122 18.0795 13.7458 16.9242 14.7385C17.594 15.8086 17.0249 17.2237 16.1531 17.6595C16.5738 19.963 15.1244 21 12.75 21C12.6213 21 12.1277 20.9905 12 20.9909C9.09511 20.9987 8.39559 19.5347 6.20011 19.1948C5.65294 19.1101 5.25 18.6375 5.25 18.0837V10.0435L5.25009 10.0434C5.25061 9.18253 5.7473 8.36114 6.58434 7.9882C7.93837 7.37911 11.0568 5.68955 11.625 4.36369C11.9905 3.51084 12.6274 3.00038 13.5 3C15.1042 2.99934 16.2071 4.64522 15.5681 6.13631C15.4001 6.52814 15.1785 6.91772 14.9047 7.30434H21.9C22.9992 7.30434 24 8.2673 24 9.35869ZM4.5 9.375V18.375C4.5 18.9963 3.99633 19.5 3.375 19.5H1.125C0.503672 19.5 0 18.9963 0 18.375V9.375C0 8.75367 0.503672 8.25 1.125 8.25H3.375C3.99633 8.25 4.5 8.75367 4.5 9.375ZM3.1875 17.25C3.1875 16.7322 2.76778 16.3125 2.25 16.3125C1.73222 16.3125 1.3125 16.7322 1.3125 17.25C1.3125 17.7678 1.73222 18.1875 2.25 18.1875C2.76778 18.1875 3.1875 17.7678 3.1875 17.25Z" fill="#212121" />
                                    </svg>
                                    <p className="text-[#212121] font-semibold text-base">{t("Note") || "Note"}</p>
                                </div>
                                <p className="w-[60ch] text-[#7F7F7F] text-sm font-light">{t("NoteText") || "Lorem Ipsum is a placeholder text commonly used in the design and publishing industries as a temporary filler."}</p>
                                <div className="w-fit flex gap-6 mt-4">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                        <path d="M13.5326 22H10.0026C9.75258 22 9.54258 21.82 9.50258 21.58L9.13258 18.93C8.50258 18.68 7.96258 18.34 7.44258 17.94L4.95258 18.95C4.73258 19.03 4.46258 18.95 4.34258 18.73L2.34258 15.27C2.28137 15.167 2.2598 15.0452 2.28188 14.9274C2.30397 14.8096 2.3682 14.7039 2.46258 14.63L4.57258 12.97C4.53258 12.65 4.50258 12.33 4.50258 12C4.50258 11.67 4.53258 11.34 4.57258 11L2.46258 9.37C2.3682 9.29613 2.30397 9.19042 2.28188 9.07263C2.2598 8.95484 2.28137 8.83304 2.34258 8.73L4.34258 5.27C4.46258 5.05 4.73258 4.96 4.95258 5.05L7.44258 6.05C7.96258 5.66 8.50258 5.32 9.13258 5.07L9.50258 2.42C9.54258 2.18 9.75258 2 10.0026 2H14.0026C14.2526 2 14.4626 2.18 14.5026 2.42L14.8726 5.07C15.5026 5.32 16.0426 5.66 16.5626 6.05L19.0526 5.05C19.2726 4.96 19.5426 5.05 19.6626 5.27L21.6626 8.73C21.7826 8.95 21.7326 9.22 21.5426 9.37L19.4326 11C19.4726 11.34 19.5026 11.67 19.5026 12V12.19C19.0026 12.07 18.5026 12 18.0026 12C17.0826 12 16.2226 12.21 15.4426 12.58C15.4726 12.39 15.5026 12.2 15.5026 12C15.5026 10.07 13.9326 8.5 12.0026 8.5C10.0726 8.5 8.50258 10.07 8.50258 12C8.50258 13.93 10.0726 15.5 12.0026 15.5C12.2026 15.5 12.3926 15.47 12.5826 15.44C12.0751 16.505 11.8944 17.6964 12.0635 18.864C12.2326 20.0315 12.7438 21.1228 13.5326 22ZM16.0026 15V21L21.0026 18L16.0026 15Z" fill="#D4D4D4" />
                                    </svg>
                                    <p className="w-[50ch] text-[#7F7F7F] text-xs font-light">{t("NoteSubtext") || "Lorem Ipsum is a placeholder text commonly used in the design and publishing industries as a temporary filler."}</p>
                                </div>
                            </div> :
                            <LawImplementationRightPanel request={data} type="audit" />
                    }
                </div>
            </>
        </TeamScope>
    );
};

export default SingleAuditLawImplementation;